import React from 'react';

import { t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { LogoWithText } from '@sm/webassets';

import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';

import EndpageHeader from '~app/components/EndpageHeader';
import EndpageImage from '~app/components/EndpageImage';
import TextBanner from '~app/components/TextBanner';

import Pages, { Page } from './pages';

import useStyles from './useStyles';
import { STANDARD_ENDPAGE_DEFAULT_COPY } from './copy';

export type Props = {
  pageName: string;
  pageData?: Page;
  utSource: string;
};

export default function StandardEndpage({ pageData = Pages, pageName, utSource }: Props): React.ReactElement {
  const {
    imageSource,
    hasPrivacyNotice,
    hasShadedBackground,
    logoRoute,
    pageCTA,
    pageCTARoute,
    pageHeading,
    pageSubheading,
    privacyRoute,
    textBannerBackgroundColor,
    textBannerCTA,
    textBannerCTARoute,
    textBannerHeading,
    textBannerSubheading,
    utSource2,
  } = pageData[pageName];

  const utSource3 = 'StandardEndpage';

  const { container, headingText, logo, subheadingText } = useStyles({ hasShadedBackground });

  return (
    <>
      <EndpageHeader
        copy={t(STANDARD_ENDPAGE_DEFAULT_COPY.ENDPAGE_HEADER)}
        hasLogo
        headerColor="sabaeus"
        headerType="thanks"
      />
      <Box className={container} display="flex" flexAlign="center" flexDirection="column" pt={6} px={5}>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              <a
                aria-label={t(STANDARD_ENDPAGE_DEFAULT_COPY.LOGO_ARIA_LABEL)}
                data-testid="StandardEndpage__LogoWithText"
                href={createURL(logoRoute, {
                  ut_source: utSource,
                  ut_source2: utSource2,
                  ut_source3: utSource3,
                  ut_ctatext: 'LogoWithText',
                })}
              >
                <LogoWithText className={logo} />
              </a>
              <Box mt={7}>
                <Typography align="center" variant="sectionTitleSm">
                  <span className={headingText}>{pageHeading}</span>
                </Typography>
              </Box>
              {hasPrivacyNotice && (
                <Box mt={5} mb={2}>
                  <a
                    data-testid="StandardEndpage__PrivacyNotice"
                    href={createURL(privacyRoute, {
                      ut_source: utSource,
                      ut_source2: utSource2,
                      ut_source3: utSource3,
                      ut_ctatext: t(STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE),
                    })}
                  >
                    <Typography align="center" variant="link">
                      {t(STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE)}
                    </Typography>
                  </a>
                </Box>
              )}
              {pageSubheading && (
                <Box mt={2}>
                  <Typography align="center">
                    <span className={subheadingText}>{pageSubheading}</span>
                  </Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
        <Box mt={6} mb={5}>
          <Button
            color="upgrade"
            href={createURL(pageCTARoute, {
              ut_source: utSource,
              ut_source2: utSource2,
              ut_source3: utSource3,
              ut_ctatext: pageCTA,
            })}
            size="lg"
          >
            {pageCTA}
          </Button>
        </Box>
      </Box>
      <Box mb={5}>
        <EndpageImage imageSource={imageSource} hasSplitBackgroundColor={hasShadedBackground} />
      </Box>
      <TextBanner
        backgroundColor={textBannerBackgroundColor}
        ctaText={textBannerCTA}
        heading={textBannerHeading}
        route={textBannerCTARoute}
        subheading={textBannerSubheading}
        utSource={utSource}
        utSource2={utSource2}
      />
    </>
  );
}
